import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import { useParams } from 'react-router-dom';
import { api } from '../apiClient';

const ReviewMockupPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mockupId: initialMockupId, mockup: initialMockup } = location.state || {};
  const { mockupId: urlMockupId } = useParams();

  const [mockup, setMockup] = useState(initialMockup);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMockupDetails = async () => {
      if (mockup) { // If full mockup object is already passed in state
        setLoading(false);
        return;
      }

      // Use the mockupId from the URL if it exists
      if (urlMockupId) {
        try {
          const response = await api.get(`/mockups/${urlMockupId}`);
          setMockup(response.data);
        } catch (err) {
          console.error('Error fetching mockup details:', err);
          setError(`Failed to load mockup details: ${err.response?.data?.detail || err.message}`);
        } finally {
          setLoading(false);
        }
      } else {
        setError('No mockup ID provided.');
        setLoading(false);
      }
    };

    fetchMockupDetails();
  }, [urlMockupId, mockup]);

  const handleProceedToPayment = () => {
    if (!mockup) {
      setError('Mockup data is not available to proceed to payment.');
      return;
    }

    const paymentDetails = {
      mockupId: mockup.id,
      quantity: 1, // Default to 1 for now, can be made configurable later
      totalAmount: parseFloat(mockup.price),
      currency: 'usd', // Assuming USD
      shippingAddress: mockup.shipping_address,
    };

    navigate('/payment', { state: paymentDetails });
  };

  if (loading) {
    return <div className="container mx-auto p-4 text-center">Loading mockup details...</div>;
  }

  if (error) {
    return <div className="container mx-auto p-4 text-center text-red-500">Error: {error}</div>;
  }

  if (!mockup) {
    return <div className="container mx-auto p-4 text-center text-red-500">Mockup not found.</div>;
  }

  return (
    <SessionAuth>
      <div className="container mx-auto p-6 bg-background rounded-lg shadow-lg">
        <h1 className="text-3xl font-bold mb-8 text-center text-gray-800">Review Your Mockup</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* Mockup Image Display */}
          <div className="border border-gray-200 rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Your Design</h2>
            <img src={mockup.mockup_image_url} alt="Generated Mockup" className="w-full h-64 object-contain mb-4" />
            <p className="text-gray-600">Prompt: {mockup.prompt}</p>
            <p className="text-gray-600">Style: {mockup.style}</p>
          </div>

          {/* Mockup Details */}
          <div className="border border-gray-200 rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Order Details</h2>
            <p className="text-gray-600">T-shirt Model: {mockup.tshirt_model_name}</p>
            <p className="text-gray-600">Variant: {mockup.tshirt_variant_id}</p>
            {mockup.image_placement && <p className="text-gray-600">Placement: {mockup.image_placement}</p>}
            {mockup.stitch_color && <p className="text-gray-600">Stitch Color: {mockup.stitch_color}</p>}
            <p className="text-gray-800 font-bold mt-4">Final Price: ${mockup.price ? mockup.price.toFixed(2) : 'N/A'}</p>
          </div>
        </div>

        {/* Shipping Address (Read-only) */}
        <div className="border border-gray-200 rounded-lg shadow-lg p-6 mb-8">
          <h2 class="text-xl font-semibold mb-4 text-gray-800">Shipping Address</h2>
          {mockup.shipping_address ? (
            <div class="text-gray-600">
              <p>{mockup.shipping_address.first_name} {mockup.shipping_address.last_name}</p>
              <p>{mockup.shipping_address.address1}</p>
              <p>{mockup.shipping_address.city}, {mockup.shipping_address.state} {mockup.shipping_address.zip}</p>
              <p>{mockup.shipping_address.country_code}</p>
              <p>Email: {mockup.shipping_address.email}</p>
              <p>Phone: {mockup.shipping_address.phone}</p>
            </div>
          ) : (
            <p class="text-gray-600">Shipping address not available.</p>
          )}
        </div>

        {/* Proceed to Payment Button */}
        <button
          onClick={handleProceedToPayment}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading || !mockup || !mockup.price}
        >
          Proceed to Payment (${mockup.price ? mockup.price.toFixed(2) : '0.00'})
        </button>
      </div>
    </SessionAuth>
  );
};

export default ReviewMockupPage;